import { Plus, Trash2, Globe, Zap } from 'lucide-react';
import type { WebhookTrigger, TriggerKind, EventFilter } from '@/types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface TriggerConfigSectionProps {
  trigger: WebhookTrigger;
  onTriggerChange: (trigger: WebhookTrigger) => void;
}

const HTTP_METHODS = ['GET', 'POST'] as const;
const SECRET_ALGOS = ['sha256', 'aes-256', 'hmac-sha256'] as const;

function defaultHttpTrigger(): WebhookTrigger {
  return { kind: 'http', methods: ['GET', 'POST'], secret: { headerName: 'x-webhook-secret', algo: 'sha256' } };
}

function defaultEventTrigger(): WebhookTrigger {
  return { kind: 'internal-event', eventFilter: [{ channel: '', eventTypes: [] }] };
}

function parseEventTypes(raw: string): string[] {
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

export function TriggerConfigSection({ trigger, onTriggerChange }: TriggerConfigSectionProps) {
  const kind: TriggerKind = trigger?.kind ?? 'http';

  const setKind = (next: TriggerKind) => {
    if (next === kind) return;
    onTriggerChange(next === 'http' ? defaultHttpTrigger() : defaultEventTrigger());
  };

  // ── HTTP config helpers ────────────────────────────────────────────────
  const http = kind === 'http' && trigger.kind === 'http' ? trigger : undefined;
  const methods = http?.methods ?? [];
  const secret = http?.secret ?? { headerName: '', algo: 'sha256' };

  const toggleMethod = (m: string) => {
    if (kind !== 'http') return;
    const next = methods.includes(m) ? methods.filter((x) => x !== m) : [...methods, m];
    onTriggerChange({ kind: 'http', methods: next, secret: trigger.kind === 'http' ? trigger.secret : undefined });
  };

  const setSecretHeader = (headerName: string) => {
    if (kind !== 'http') return;
    onTriggerChange({ kind: 'http', methods, secret: { ...secret, headerName } });
  };

  const setSecretAlgo = (algo: string) => {
    if (kind !== 'http') return;
    onTriggerChange({ kind: 'http', methods, secret: { ...secret, algo } });
  };

  // ── Internal-event config helpers ──────────────────────────────────────
  const filters: EventFilter[] =
    kind === 'internal-event' && trigger.kind === 'internal-event' ? trigger.eventFilter ?? [] : [];

  const setFilters = (next: EventFilter[]) => {
    onTriggerChange({ kind: 'internal-event', eventFilter: next });
  };

  const updateFilter = (i: number, patch: Partial<EventFilter>) => {
    setFilters(filters.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  };

  const addFilter = () => setFilters([...filters, { channel: '', eventTypes: [] }]);
  const removeFilter = (i: number) => setFilters(filters.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <Label>Trigger kind</Label>
      <div className="flex rounded-md border">
        <Button
          type="button"
          variant={kind === 'http' ? 'default' : 'ghost'}
          size="sm"
          className="flex-1 gap-1.5"
          onClick={() => setKind('http')}
        >
          <Globe className="h-4 w-4" /> HTTP
        </Button>
        <Button
          type="button"
          variant={kind === 'internal-event' ? 'default' : 'ghost'}
          size="sm"
          className="flex-1 gap-1.5"
          onClick={() => setKind('internal-event')}
        >
          <Zap className="h-4 w-4" /> Internal event
        </Button>
      </div>

      {kind === 'http' ? (
        <div className="space-y-3 pt-1">
          {/* Methods */}
          <div className="space-y-2">
            <Label>Methods</Label>
            <div className="flex flex-wrap gap-2">
              {HTTP_METHODS.map((m) => (
                <label
                  key={m}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                    methods.includes(m)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={methods.includes(m)}
                    onChange={() => toggleMethod(m)}
                    className="h-3.5 w-3.5 accent-primary"
                  />
                  {m}
                </label>
              ))}
            </div>
          </div>

          {/* Secret */}
          <div className="space-y-2">
            <Label>Secret</Label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Header name</span>
                <Input
                  value={secret.headerName}
                  onChange={(e) => setSecretHeader(e.target.value)}
                  placeholder="x-webhook-secret"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Algorithm</span>
                <Select value={secret.algo} onValueChange={setSecretAlgo}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECRET_ALGOS.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between">
            <Label>Event filters</Label>
            <Button type="button" variant="outline" size="sm" onClick={addFilter}>
              <Plus className="mr-1 h-3 w-3" /> Add filter
            </Button>
          </div>
          {filters.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              No event filters. Add one to listen for internal events.
            </p>
          )}
          {filters.map((filter, i) => (
            <div key={i} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Filter #{i + 1}</span>
                <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFilter(i)}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  value={filter.channel}
                  onChange={(e) => updateFilter(i, { channel: e.target.value })}
                  placeholder="Channel (e.g. subjects.registered)"
                />
                <Input
                  value={filter.eventTypes.join(', ')}
                  onChange={(e) => updateFilter(i, { eventTypes: parseEventTypes(e.target.value) })}
                  placeholder="Event types (comma-separated)"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}